import React, { memo, ChangeEvent } from 'react'
import { Checkbox, Radio } from 'components/forms'
import { DragHandleIcon } from 'transfix-icons'
import { IStickyHeaderStyleProps } from './Table'

export interface ITableCellProps extends JSX.ElementChildrenAttribute {
  tableCellType?: string
  type?: 'counter' | 'icon' | 'number' | 'checkbox' | 'radio'
  ratio?: number[] // [all, med-and-up, large-and-up]
  selected?: boolean
  semiSelected?: boolean
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  label?: string
  value?: any
  colSpan?: number
  dataTest?: string
  isDraggable?: boolean
  className?: string
  spanClassName?: string
  stickyHeader?: boolean
  stickyHeaderStyle?: IStickyHeaderStyleProps
}

export const TableCell = memo<ITableCellProps>(
  ({
    tableCellType = 'td',
    children,
    type,
    ratio,
    selected,
    semiSelected,
    onChange,
    label,
    value,
    colSpan,
    dataTest,
    isDraggable = false,
    className,
    spanClassName,
    stickyHeader = false,
    stickyHeaderStyle = { top: '0px', 'z-index': 2 },
  }) => {
    const cellClasses = className ? [className] : []
    const [allRatio, medAndUpRatio, largeAndUpRatio] = ratio || []

    if (type) {
      cellClasses.push(`cell-${type}`)
    }

    if (allRatio) {
      ;['xs', 'sm', 'md', 'lg', 'xl'].forEach(size => {
        switch (size) {
          case 'xs':
          case 'sm':
            cellClasses.push(`cell-${size}-${allRatio}`)
            break
          case 'md':
            cellClasses.push(`cell-${size}-${medAndUpRatio || allRatio}`)
            break
          case 'lg':
          case 'xl':
            cellClasses.push(`cell-${size}-${largeAndUpRatio || medAndUpRatio || allRatio}`)
            break
          default:
            break
        }
      })
    }

    if (type === 'checkbox') {
      if (tableCellType === 'th') {
        children = (
          <Checkbox
            semiChecked={semiSelected}
            value={value}
            checked={selected}
            onChange={onChange}
          />
        )
      } else {
        children = <Checkbox label={label} value={value} checked={selected} onChange={onChange} />
      }
    }
    if (type === 'radio' && tableCellType === 'td' && onChange) {
      children = (
        <Radio id={value} label={label} value={value} checked={!!selected} onChange={onChange} />
      )
    }

    if (isDraggable) {
      children = (
        <div className='flex items-center'>
          <DragHandleIcon className='drag-n-drop-handle' />{' '}
          <span className={spanClassName}>{children}</span>
        </div>
      )
    }

    const props: { className?: string; colSpan?: number; 'data-test'?: string; style?: {} } = {}

    if (cellClasses.length > 0) {
      props.className = cellClasses.join(' ')
    }

    if (colSpan) {
      props.colSpan = colSpan
    }

    if (dataTest) {
      props['data-test'] = dataTest
    }

    if (stickyHeader) {
      props.className = 'sticky-header'
      if (stickyHeaderStyle) {
        props.style = stickyHeaderStyle
      }
    }

    return React.createElement(tableCellType, props, children)
  }
)
